import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConjugationLeaderboard } from "@/components/conjugation/ConjugationLeaderboard";
import { StreakCard, VerbOfDayCard, WeakVerbsCard, ChallengeCard } from "@/components/conjugation/PracticeCoach";
import { SentencePrompt } from "@/components/conjugation/SentencePrompt";
import { LinkedPrompt } from "@/components/conjugation/LinkedPrompt";
import { WorksheetDialog } from "@/components/conjugation/WorksheetDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X, Trophy, RotateCcw, Sparkles, Shuffle, PenLine, ListOrdered, Link2, Printer, ChevronDown } from "lucide-react";

const STEM_GROUPS = ["e_ie", "e_i", "o_ue", "u_ue", "zco", "i_i"];
const FAMILIES = [
  { key: "regular", label: "cj_fam_regular" },
  { key: "ortografico", label: "cj_fam_ortho" },
  { key: "irregular", label: "cj_fam_core" },
  { key: "stem", label: "cj_fam_stem" },
  { key: "reflexivo", label: "cj_fam_reflexive" },
];

export const ConjugationPractice = ({ showVos, showVosotros, onTogglePerson }) => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const lbl = (o) => (lang === "en" ? (o?.label_en || o?.label) : o?.label);
  const [options, setOptions] = useState(null);
  const [mode, setMode] = useState("forms");
  const [level, setLevel] = useState(1);
  const [hardMode, setHardMode] = useState(false);
  const [mixed, setMixed] = useState(false);
  const [tenses, setTenses] = useState(["presente"]);
  const [difficulty, setDifficulty] = useState("all");
  const [fams, setFams] = useState([]);
  const [stemSel, setStemSel] = useState([]);
  const [count, setCount] = useState("10");
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [value, setValue] = useState("");
  const [value2, setValue2] = useState("");
  const [connector, setConnector] = useState("");
  const [pronoun, setPronoun] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [helped, setHelped] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showOpts, setShowOpts] = useState(false);
  const [coach, setCoach] = useState(null);
  const [quick, setQuick] = useState(null);   // última ronda rápida, para repetirla igual
  const inputRef = useRef(null);

  const loadHistory = () => api.get("/conjugation/practice/history")
    .then((r) => setHistory(r.data)).catch(() => {});
  const loadCoach = () => api.get("/conjugation/practice/coach")
    .then((r) => setCoach(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/conjugation/practice/options").then((r) => setOptions(r.data)).catch(() => {});
    loadHistory();
    loadCoach();
  }, []);

  const toggleTense = (key) => setTenses((prev) =>
    prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]);

  const countOf = (key) => (options?.groups || []).find((g) => g.key === key)?.count || 0;
  const stemCount = STEM_GROUPS.reduce((a, k) => a + countOf(k), 0);
  const toggleFam = (key) => setFams((prev) => {
    if (prev.includes(key)) {
      if (key === "stem") setStemSel([]);
      return prev.filter((f) => f !== key);
    }
    return [...prev, key];
  });
  const toggleStem = (key) => setStemSel((prev) =>
    prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]);
  const selectedGroups = () => {
    if (!fams.length) return [];
    const out = fams.filter((f) => f !== "stem");
    if (fams.includes("stem")) out.push(...(stemSel.length ? stemSel : STEM_GROUPS));
    return out;
  };
  const poolSize = () => {
    const g = selectedGroups();
    return g.length ? g.reduce((a, k) => a + countOf(k), 0) : (options?.total_verbs || 0);
  };

  /** Línea corta con lo que está elegido, para no mostrar todo el formulario de entrada. */
  const resumen = (() => {
    const tiempos = mixed ? [t("cj_tense_mixed")]
      : tenses.map((k) => lbl((options?.tenses || []).find((x) => x.key === k)) || k);
    const fam = !fams.length ? `${t("cj_fam_all")} (${options?.total_verbs || 0})`
      : fams.map((f) => t(FAMILIES.find((x) => x.key === f)?.label || f)).join(" + ");
    const dif = difficulty === "all" ? t("cj_opts_all_levels")
      : lbl((options?.difficulties || []).find((d) => d.key === difficulty));
    return [...(tiempos.length ? [tiempos.join(" · ")] : []), fam, dif,
            t("cj_opts_questions", { n: count })].filter(Boolean);
  })();

  const start = async () => {
    if (!mixed && !tenses.length) { toast.error(t("cj_pick_tense")); return; }
    setBusy(true);
    setQuick(null);
    try {
      const { data } = await api.post("/conjugation/practice/start", {
        tenses: mixed ? ["mixed"] : tenses,
        difficulties: difficulty === "all" ? [] : [difficulty],
        groups: selectedGroups(),
        include_vos: showVos,
        include_vosotros: showVosotros,
        count: Number(count),
        mode,
        hard_mode: hardMode,
        level,
      });
      setSession(data); setIdx(0); setValue(""); setValue2(""); setConnector(""); setPronoun("");
      setFeedback(null); setScore(0); setResult(null); setHelped([]);
      setTimeout(() => inputRef.current?.focus(), 150);
    } catch (e) {
      toast.error(lang === "en" ? t("cj_cannot_build") : (e?.response?.data?.detail || t("cj_cannot_build")));
    } finally { setBusy(false); }
  };

  /** Rondas de un toque: verbo del día, verbos flojos y reto semanal. */
  const quickStart = async (extra) => {
    setBusy(true);
    setQuick(extra);
    try {
      const { data } = await api.post("/conjugation/practice/start", {
        mode: "forms", difficulties: [], groups: [],
        include_vos: showVos, include_vosotros: showVosotros,
        count: 10, level: 1, hard_mode: false, ...extra,
      });
      setSession(data); setIdx(0); setValue(""); setValue2(""); setConnector(""); setPronoun("");
      setFeedback(null); setScore(0); setResult(null); setHelped([]);
      setTimeout(() => inputRef.current?.focus(), 150);
    } catch (e) {
      toast.error(lang === "en" ? t("cj_cannot_build") : (e?.response?.data?.detail || t("cj_cannot_build")));
    } finally { setBusy(false); }
  };

  const useHelp = () => {    if (!session || feedback || helped.includes(idx)) return;
    setHelped((prev) => [...prev, idx]);
    api.post(`/conjugation/practice/${session.session_id}/help`, { index: idx }).catch(() => {});
  };

  const send = async () => {
    if (feedback || !value.trim()) return;
    const { data } = await api.post(`/conjugation/practice/${session.session_id}/answer`,
      { index: idx, answer: value, pronoun, answer2: value2, connector });
    setFeedback(data);
    setScore(data.score);
  };

  const next = async () => {
    if (idx + 1 >= session.total) {
      const { data } = await api.post(`/conjugation/practice/${session.session_id}/finish`);
      setResult(data);
      loadHistory();
      loadCoach();
      return;
    }
    setIdx(idx + 1); setValue(""); setValue2(""); setConnector(""); setPronoun("");
    setFeedback(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const q = session?.questions?.[idx];

  if (result) {
    return (
      <Card className="p-8 max-w-2xl" data-testid="practice-result">
        <div className="flex items-center gap-3 mb-5">
          <Trophy className="h-7 w-7 text-amber-500" />
          <div>
            <h3 className="text-xl font-semibold">
              {t("cj_points_of", { score: result.score, max: result.max_score })}
            </h3>
            <p className="text-sm text-slate-500">
              {t("cj_correct_of", { correct: result.correct, total: result.total, acc: result.accuracy })}
              {result.accent_only > 0 && ` · ${result.accent_only} ${t("cj_accent_wrong")}`}
            </p>
          </div>
        </div>
        {result.misses.length > 0 && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t("cj_to_review")}</p>
            <div className="space-y-2">
              {result.misses.map((m, i) => (
                <div key={i} className="text-sm border-b border-slate-100 pb-2"
                     data-testid={`practice-miss-${i}`}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <strong>{m.verb}</strong>
                    <span className="text-slate-500">{lbl({ label: m.tense_label, label_en: m.tense_label_en })} · {m.person_label}</span>
                    <span className="ml-auto">
                      <span className="line-through text-rose-500 mr-2">{m.answer || "—"}</span>
                      <strong className="text-emerald-700">
                        {m.expected_pronoun ? `${m.expected_pronoun} ` : ""}{m.expected}
                      </strong>
                      {m.accent_only && <span className="text-amber-600 text-xs ml-2">{t("cj_accent_only")}</span>}
                    </span>
                  </div>
                  {m.sentence && <p className="text-xs text-slate-400 mt-0.5">{m.sentence}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <Button onClick={() => (quick ? quickStart(quick) : start())} data-testid="practice-again-btn">
            <RotateCcw className="h-4 w-4" /> {t("cj_practice_again")}
          </Button>
          <Button variant="outline" onClick={() => { setSession(null); setResult(null); setQuick(null); }}
                  data-testid="practice-change-filters-btn">
            {t("cj_change_filters")}
          </Button>
        </div>
      </Card>
    );
  }

  if (session && q) {
    return (
      <Card className="p-8 max-w-2xl" data-testid="practice-question">
        <div className="flex items-center justify-between text-sm text-slate-500 mb-6">
          <span data-testid="practice-progress">{t("cj_question_of", { n: idx + 1, total: session.total })}</span>
          <span className="font-semibold text-slate-900" data-testid="practice-score">{score} {t("cj_pts")}</span>
        </div>
        {q.linked ? (
          <>
            <p className="text-sm text-slate-500 mb-4">
              <strong className="text-slate-700">{lbl({ label: q.tense_label, label_en: q.tense_label_en })}</strong>
              {!q.linked.same_tense && <> → <strong className="text-slate-700">{lbl({ label: q.tense2_label, label_en: q.tense2_label_en })}</strong></>}
              {" · "}{q.person_label} / {q.person2_label}
            </p>
            <LinkedPrompt
              ref={inputRef}
              linked={q.linked} level={q.level} gloss={q.gloss} onHelp={useHelp}
              hint={q.hint} hint2={q.hint2} options={q.connector_options}
              value={value} onChange={setValue}
              value2={value2} onChange2={setValue2}
              connector={connector} onConnector={setConnector}
              disabled={!!feedback}
              onEnter={() => (feedback ? next() : send())}
            />
          </>
        ) : q.sentence ? (
          <>
            <p className="text-sm text-slate-500 mb-4">
              <strong className="text-slate-700">{lbl({ label: q.tense_label, label_en: q.tense_label_en })}</strong>
              {" · "}{q.person_label}
            </p>
            <SentencePrompt
              ref={inputRef}
              sentence={q.sentence}
              gloss={q.gloss}
              onHelp={useHelp}
              hint={q.hint}
              hardMode={q.hard_mode}
              value={value}
              onChange={setValue}
              pronoun={pronoun}
              onPronoun={setPronoun}
              disabled={!!feedback}
              onEnter={() => (feedback ? next() : send())}
            />
          </>
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-3xl font-semibold text-slate-900">{q.verb}</h3>
                <Badge variant="outline">{q.group}</Badge>
              </div>
              <p className="text-sm text-slate-500">{q.translation}</p>
            </div>
            <p className="text-base text-slate-700 mb-2">
              <strong>{lbl({ label: q.tense_label, label_en: q.tense_label_en })}</strong> · {q.person_label}
            </p>
            <Input
              ref={inputRef}
              value={value}
              disabled={!!feedback}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") (feedback ? next() : send()); }}
              placeholder={t("cj_type_answer")}
              className="text-lg py-6 bg-white"
              data-testid="practice-input"
            />
          </>
        )}
        {(q.sentence || q.linked) && (
          <p className="mt-4 text-xs text-slate-400" data-testid="practice-help-note">
            {helped.includes(idx) ? t("cj_help_used") : t("cj_help_hint")}
          </p>
        )}
        {feedback && (
          <div className={`mt-4 rounded-lg p-3 text-sm flex items-start gap-2 ${
            feedback.correct ? "bg-emerald-50 text-emerald-800"
              : feedback.accent_only ? "bg-amber-50 text-amber-800" : "bg-rose-50 text-rose-800"}`}
               data-testid="practice-feedback">
            {feedback.correct ? <Check className="h-4 w-4 mt-0.5" /> : <X className="h-4 w-4 mt-0.5" />}
            <span>
              {feedback.correct && t("cj_correct")}
              {!feedback.correct && feedback.expected2 !== undefined ? (
                <>
                  <span className="block">{t("cj_right_forms")}</span>
                  <span className="block">1 · <strong>{feedback.expected}</strong>
                    {feedback.accent_only && ` (${t("cj_accent_only")})`}</span>
                  <span className="block">2 · <strong>{feedback.expected2}</strong>
                    {feedback.accent_only2 && ` (${t("cj_accent_only")})`}</span>
                </>
              ) : (
                <>
                  {feedback.accent_only && <>{t("cj_almost_accent")} <strong>{feedback.expected}</strong>.</>}
                  {!feedback.correct && !feedback.accent_only && (
                    <>{t("cj_right_form")} <strong>
                      {feedback.expected_pronoun ? `${feedback.expected_pronoun} ` : ""}{feedback.expected}
                    </strong>.</>
                  )}
                </>
              )}
              {feedback.pronoun_correct === false && (
                <span className="block text-xs mt-1">{t("cj_pronoun_wrong")}</span>
              )}
              {feedback.points > 0 && ` +${feedback.points} ${t("cj_pts")}`}
            </span>
          </div>
        )}
        <div className="mt-6 flex gap-3">
          {!feedback
            ? <Button onClick={send} disabled={!value.trim()} data-testid="practice-send-btn">{t("cj_check")}</Button>
            : <Button onClick={next} data-testid="practice-next-btn">
                {idx + 1 >= session.total ? t("cj_see_score") : t("cj_next")}
              </Button>}
          <Button variant="ghost" onClick={() => setSession(null)}>{t("cj_exit")}</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_370px] gap-5 items-start pb-28 sm:pb-0"
         data-testid="practice-setup">
      <div className="space-y-4 min-w-0">
      {history?.practices > 0 && (
        <Card className="p-4 flex flex-wrap gap-6 text-sm" data-testid="practice-history">
          <span><strong>{history.practices}</strong> {t("cj_practices")}</span>
          <span>{t("cj_best_score")}: <strong className="text-amber-600">{history.best_score}</strong></span>
          <span><strong>{history.correct}</strong> {t("cj_hits_of")} {history.answered}</span>
        </Card>
      )}
      <Card className="p-5 sm:p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-orange-500" /> {t("cj_build")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {[
            { key: "forms", icon: ListOrdered, title: t("cj_mode_forms"), desc: t("cj_mode_forms_desc") },
            { key: "sentences", icon: PenLine, title: t("cj_mode_sentences"), desc: t("cj_mode_sentences_desc") },
            { key: "linked", icon: Link2, title: t("cj_mode_linked"), desc: t("cj_mode_linked_desc") },
          ].map((m) => (
            <button key={m.key} onClick={() => setMode(m.key)} data-testid={`practice-mode-${m.key}`}
                    className={`text-left rounded-xl border-2 p-3 transition-all ${
                      mode === m.key ? "border-orange-400 bg-orange-50/60 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
                <m.icon className={`h-4 w-4 ${mode === m.key ? "text-orange-500" : "text-slate-400"}`} />
                {m.title}
              </span>
              <span className="block text-xs text-slate-500 mt-1">{m.desc}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed
                        border-slate-300 bg-slate-50 px-4 py-3 mb-5" data-testid="practice-summary">
          <div className="flex flex-wrap gap-1.5">
            {resumen.map((c) => (
              <span key={c} className="text-xs font-semibold bg-white border border-slate-200 rounded-full px-2.5 py-1">
                {c}
              </span>
            ))}
          </div>
          <button onClick={() => setShowOpts((v) => !v)} data-testid="practice-toggle-options"
                  className="text-[13px] font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1">
            {showOpts ? t("cj_opts_hide") : t("cj_opts_show")}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showOpts ? "rotate-180" : ""}`} />
          </button>
        </div>
        {showOpts && (
        <div data-testid="practice-options">
        {mode === "linked" && (
          <div className="mb-5 rounded-xl border border-violet-200 bg-violet-50/50 p-3"
               data-testid="linked-levels">
            <p className="text-xs uppercase tracking-wide text-violet-700 mb-2">{t("cj_level")}</p>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3].map((n) => (
                <button key={n} onClick={() => setLevel(n)} data-testid={`practice-level-${n}`}
                        className={`text-left rounded-lg border px-3 py-2 text-xs transition-colors flex-1 min-w-[150px] ${
                          level === n ? "bg-violet-600 text-white border-violet-600"
                            : "bg-white text-slate-700 border-violet-200 hover:border-violet-400"}`}>
                  <span className="block font-semibold">{t(`cj_level_${n}`)}</span>
                  <span className={`block mt-0.5 ${level === n ? "text-violet-100" : "text-slate-500"}`}>
                    {t(`cj_level_${n}_desc`)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        {(mode !== "linked" || level !== 2) && (
          <>
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t("cj_tenses")}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              <button onClick={() => setMixed((v) => !v)} data-testid="practice-tense-mixed"
                      className={`px-3 py-1.5 rounded-full border text-sm transition-colors flex items-center gap-1.5 ${
                        mixed ? "bg-violet-600 text-white border-violet-600"
                          : "bg-white text-violet-700 border-violet-300 hover:border-violet-500"}`}>
                <Shuffle className="h-3.5 w-3.5" /> {t("cj_tense_mixed")}
              </button>
              {(options?.tenses || []).map((tn) => (
                <button key={tn.key} onClick={() => { setMixed(false); toggleTense(tn.key); }}
                        data-testid={`practice-tense-${tn.key}`}
                        className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                          !mixed && tenses.includes(tn.key)
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"} ${
                          mixed ? "opacity-40" : ""}`}>
                  {lbl(tn)}
                </button>
              ))}
            </div>
          </>
        )}
        <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t("cj_which_verbs")}</p>
        <div className="flex flex-wrap gap-2 mb-2" data-testid="practice-families">
          <button onClick={() => { setFams([]); setStemSel([]); }} data-testid="practice-family-all"
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    !fams.length ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-emerald-700 border-emerald-300 hover:border-emerald-500"}`}>
            {t("cj_fam_all")} ({options?.total_verbs || 0})
          </button>
          {FAMILIES.map((f) => (
            <button key={f.key} onClick={() => toggleFam(f.key)}
                    data-testid={`practice-family-${f.key}`}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      fams.includes(f.key) ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"}`}>
              {t(f.label)} ({f.key === "stem" ? stemCount : countOf(f.key)})
            </button>
          ))}
        </div>
        {fams.includes("stem") && (
          <div className="flex flex-wrap gap-2 mb-2 pl-1" data-testid="practice-stem-groups">
            {STEM_GROUPS.filter((k) => countOf(k) > 0).map((k) => {
              const g = (options?.groups || []).find((x) => x.key === k);
              const short = (lbl(g) || k).split(":").pop().trim();
              return (
                <button key={k} onClick={() => toggleStem(k)} data-testid={`practice-stem-${k}`}
                        className={`px-2.5 py-1 rounded-lg border text-xs transition-colors ${
                          stemSel.includes(k) ? "bg-emerald-100 text-emerald-800 border-emerald-400"
                            : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
                  {short} ({g?.count || 0})
                </button>
              );
            })}
          </div>
        )}
        {poolSize() > 0 && poolSize() < 5 && (
          <p className="text-xs text-amber-600 mb-4" data-testid="practice-few-verbs">
            {t("cj_fam_few")}
          </p>
        )}
        <div className="flex flex-wrap gap-4 items-end mt-4">
          <div>
            <Label className="text-xs">{t("cj_difficulty")}</Label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger className="w-[170px] mt-1 bg-white" data-testid="practice-difficulty">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("cj_difficulty_all")}</SelectItem>
                {(options?.difficulties || []).map((d) => (
                  <SelectItem key={d.key} value={d.key}>{lbl(d)} ({d.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("cj_count")}</Label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger className="w-[120px] mt-1 bg-white" data-testid="practice-count">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["5", "10", "20", "30"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Label htmlFor="p-vos" className="text-xs">{t("cj_include_vos")}</Label>
            <Switch id="p-vos" checked={showVos} data-testid="practice-vos-switch"
                    onCheckedChange={(v) => onTogglePerson("vos", v)} />
            <Label htmlFor="p-vosotros" className="text-xs ml-3">{t("cj_include_vosotros")}</Label>
            <Switch id="p-vosotros" checked={showVosotros} data-testid="practice-vosotros-switch"
                    onCheckedChange={(v) => onTogglePerson("vosotros", v)} />
          </div>
        </div>
        {(mode === "sentences" || mode === "linked") && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
            <Switch id="p-hard" checked={hardMode} data-testid="practice-hard-switch"
                    onCheckedChange={setHardMode} />
            <Label htmlFor="p-hard" className="text-xs">{t("cj_hard_mode")}</Label>
            <span className="text-xs text-slate-400">· {t("cj_hard_mode_desc")}</span>
          </div>
        )}
        </div>
        )}
        <div className="hidden sm:flex gap-2 mt-6">
          <Button onClick={start} disabled={busy} data-testid="practice-start-btn">
            {t("cj_start")}
          </Button>
          <Button variant="outline" onClick={() => setSheetOpen(true)}
                  data-testid="practice-worksheet-btn">
            <Printer className="h-4 w-4" /> {t("cj_worksheet_open")}
          </Button>
        </div>
      </Card>
      <WorksheetDialog open={sheetOpen} onOpenChange={setSheetOpen} payload={{
        tenses: mixed ? ["mixed"] : tenses,
        difficulties: difficulty === "all" ? [] : [difficulty],
        groups: selectedGroups(),
        include_vos: showVos, include_vosotros: showVosotros,
        count: Number(count), mode, hard_mode: hardMode, level,
      }} />
        <VerbOfDayCard vod={coach?.verb_of_day} busy={busy}
                       onStart={() => quickStart({
                         tenses: [coach.verb_of_day.tense], verbs: [coach.verb_of_day.verb],
                         count: coach.verb_of_day.count || 5 })} />
        <WeakVerbsCard verbs={coach?.weak_verbs} busy={busy}
                       onStart={() => quickStart({
                         tenses: ["mixed"], verbs: coach.weak_verbs.map((v) => v.verb),
                         count: 10 })} />
      </div>
      <div className="space-y-4 lg:sticky lg:top-4" data-testid="practice-side">
        <StreakCard streak={coach?.streak} />
        <ConjugationLeaderboard myUserId={user?.user_id} refreshKey={history?.practices || 0} />
        <ChallengeCard challenge={coach?.challenge} busy={busy}
                       onStart={() => quickStart({
                         tenses: [coach.challenge.tense], count: 10 })} />
      </div>

      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 px-4 pt-6 pb-4
                      bg-gradient-to-t from-white via-white to-transparent">
        <Button className="w-full h-12 text-base shadow-lg" onClick={start} disabled={busy}
                data-testid="practice-start-btn-mobile">
          {t("cj_start")}
        </Button>
      </div>
    </div>
  );
};
